import {ChangeDetectionStrategy, Component, EventEmitter, Input, Output} from '@angular/core';
import {MatSelectChange} from '@angular/material/select';
import {Router} from '@angular/router';
import {BehaviorSubject, firstValueFrom} from 'rxjs';
import {distinctUntilChanged, filter, first, map, switchMap} from 'rxjs/operators';

import {AuditsFiltersMeta} from '@sim/audit/models/audit';
import {Collection, CollectionClassName} from '@sim/collections/models/collection';
import {UICollectionService} from '@sim/collections/services/ui-collection.service';
import {UsersApiService} from '@sim/users/services/users-api.service';
import {makeQueryBuilder, makeQueryRules} from '@ui/ui.module';

import {CardType, ExportsApiSDispatcherervice} from '@atmi/core';
import {SettingsClassName, Undefinable} from '@atmi/core/models';
import {Status} from '@atmi/core/models/status';
import {SettingsApiService} from '@atmi/core/services/settings-api.service';
import {UIExportService} from '@atmi/core/services/ui-export.service';
import {InSubject, required} from '@atmi/core/utils';
import {CollectionApiService, CollectionItemApiService} from "@sim/collections/services";

interface Entity {
  user: string;
  createdAt: string;
  updatedAt: string;
  status: Status;
}

/**
 * This component is able to be use with each entity that have:
 *
 * - an `user` property related to the id
 * - a `createdAt` and `updatedAt` properties
 * - a `status` property
 */
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-card-actions',
  styleUrls: ['./card-actions.component.scss'],
  templateUrl: './card-actions.component.html',
})
export class CardActionsComponent<T extends Entity> {

  @Input() @InSubject() entity?: T;
  private readonly entityChange = new BehaviorSubject<Undefinable<Entity> | null>(undefined);

  @Input() set entityId(id: string) {
    this._entityId = id;
    //this.getCollections();
  };

  @Input() cardType?: CardType;
  @Input() cardName = '';

  _entityId: string = '';

  collectionsLoading = false;//rimossa lista collezioni come da richiesta in data 8/4/2024

  get entityId(): string {
    return this._entityId;
  }

  // tslint:disable-next-line: variable-name
  private _className?: CollectionClassName | SettingsClassName;
  @Input() set className(value: CollectionClassName | SettingsClassName | undefined) {
    this._className = value;
    this.isExportEnable = this.settingsApi.selectStateSnapshot().system?.layouts.some((l) => l.className === value) || false;
  }

  get className() {
    return this._className;
  }

  collections: Collection[] = [];

  isExportEnable = false;

  readonly username$ = this.entityChange.pipe(
    filter((entity): entity is Entity => !!entity),
    map((entity) => entity.user),
    filter((userId): userId is string => !!userId),
    distinctUntilChanged(),
    switchMap((userId) => this.usersApi.get(userId)),
    map((user) => user.username),
  );


  @Output() readonly changeStatus = new EventEmitter<Status>();

  readonly statuses = [
    {value: Status.STATUS_DRAFT, label: 'Bozza'},
    {value: Status.STATUS_PUBLISH, label: 'Pubblicato'},
    {value: Status.STATUS_PRIVATE, label: 'Privato'},
  ];

  constructor(
    private readonly exportApiDispatcher: ExportsApiSDispatcherervice,
    private readonly router: Router,
    private readonly settingsApi: SettingsApiService,
    private readonly uiExportService: UIExportService,
    private readonly usersApi: UsersApiService,
    private readonly collectionApiService: CollectionApiService,
    private readonly collectionItemApi: CollectionItemApiService,
    readonly uiCollectionService: UICollectionService,
  ) {
  }

  private async getCollections() {
    try {
      const res = await firstValueFrom(this.collectionApiService.index().pipe(first()));
      const collections = res.data.filter(c => Boolean(c.id));

      for (const collection of collections) {
        const collectionItems = await firstValueFrom(this.collectionItemApi.index(collection?.id || '').pipe(first()))
        if (collectionItems.data.findIndex((item) => item.objectId === this._entityId) !== -1) {
          this.collections.push(collection);
        }

      }
    } catch (e) {
      console.error(e);
    } finally {
      this.collectionsLoading = false;
    }

  }

  async exportHandler() {
    required(this.entityId);
    required(this.className);

    this.uiExportService.manageExport(
      (template?: string, format?: string) => {
        required(this.entityId);
        required(this.className);

        return this.exportApiDispatcher.dispatchExport(
          this.className,
          this.entityId,
          template,
          format,
          this.cardType,
        );
      },
      this.className,
      this.exportApiDispatcher.getExportName(this.className, this.cardName, this.cardType),
    );
  }

  changeStatusHandler(event: MatSelectChange) {
    this.changeStatus.emit(event.value);
  }

  showAudits() {
    const rules = makeQueryRules({objectId: this.entityId, className: this.className}, AuditsFiltersMeta);
    const query = makeQueryBuilder(rules);
    const q = JSON.stringify(query);
    const queryParams = {q};
    this.router.navigate(['/audits'], {queryParams});
  }
}
